import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Colors from '../../library/Colors';
import { DesignVariantSwitch } from './_designVariant';

// Shared building blocks for the webshop-admin screens, so they stop
// re-declaring (and drifting on) the same tokens/components. Keeps the existing
// hex-token approach (deliberately not the big theme-token migration) but in
// ONE place, so every screen resolves the same values.

const hexToRgba = (hex, a) => {
    const h = String(hex).replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
};

// The token block that used to live (verbatim) in every screen.
export const webshopTokens = (theme) => {
    const isDark = theme === 'dark';
    return {
        isDark,
        cardBg: isDark ? '#2d333b' : '#ffffff',
        borderColor: isDark ? '#3a3f47' : '#e4e7eb',
        textColor: isDark ? Colors.dark_text : Colors.light_text,
        mutedText: isDark ? '#b0b8c1' : '#768390',
        panelBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    };
};

// One modal-sizing system for the webshop-admin content/editor dialogs, so the
// Opmaak section modals and the Pages modals (block config, product container,
// designers) all read at the same generous width instead of drifting per-modal.
// Spread the result straight onto a MUI <Dialog>: {...webshopModalSizing('wide')}.
//
//   wide    – multi-column card modals + full editors (Menu bar, Footer, Product
//             detail, product container, block designers). Fills wide screens.
//   medium  – single-card / focused config (Image gallery, Global widths,
//             Language bar). Roomy, but not so wide it reads as empty.
//   compact – short forms and confirmations.
//
// Pass { collapsed: true } (sidebar hidden) to let wide modals grow a touch more.
const MODAL_TIERS = {
    wide:   { width: '94vw', widthCollapsed: '96vw', max: 1700, maxCollapsed: 1850 },
    medium: { width: '92vw', widthCollapsed: '92vw', max: 1280, maxCollapsed: 1280 },
};
export const webshopModalSizing = (tier = 'compact', { collapsed = false, sx = {} } = {}) => {
    const t = MODAL_TIERS[tier];
    if (!t) {
        // compact / unknown → MUI's own small breakpoint.
        return { maxWidth: 'sm', fullWidth: true, PaperProps: { sx: { borderRadius: 3, maxHeight: '92vh', ...sx } } };
    }
    return {
        maxWidth: false,
        fullWidth: true,
        PaperProps: { sx: {
            borderRadius: 3,
            maxHeight: '92vh',
            width: collapsed ? t.widthCollapsed : t.width,
            maxWidth: collapsed ? t.maxCollapsed : t.max,
            ...sx,
        } },
    };
};

// Tinted icon "medallion". The background tint is derived from the SAME colour
// as the glyph (brand blue by default), so the two blues no longer clash.
export const iconBoxSx = (isDark, color = Colors.sft_blue) => ({
    width: 36,
    height: 36,
    borderRadius: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: hexToRgba(color, isDark ? 0.16 : 0.09),
    color,
    flexShrink: 0,
    '& svg': { fontSize: 20 },
});

// One page-title component: "h4 / 500" heading + muted body2 description, above
// the content card. Optional right-aligned `action` slot.
export const WebshopPageHeader = ({ title, description, theme, action, t }) => {
    const { textColor, mutedText } = webshopTokens(theme);
    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>{title}</Typography>
                {description && <Typography variant="body2" sx={{ color: mutedText }}>{description}</Typography>}
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Demo switch between design variants — renders nothing unless
                    the provider enabled it for this user (SoftTouch only). */}
                <DesignVariantSwitch t={t} />
                {action}
            </Box>
        </Box>
    );
};
WebshopPageHeader.propTypes = {
    title: PropTypes.node,
    description: PropTypes.node,
    theme: PropTypes.string,
    action: PropTypes.node,
    t: PropTypes.func,
};

// One password field with a show/hide toggle.
export const WebshopPasswordField = ({ label, value, onChange, visible, onToggle, placeholder, size = 'small', fullWidth = true, ...rest }) => (
    <TextField
        label={label}
        size={size}
        fullWidth={fullWidth}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton size="small" onClick={onToggle} edge="end" tabIndex={-1}>
                        {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                </InputAdornment>
            ),
        }}
        {...rest}
    />
);
WebshopPasswordField.propTypes = {
    label: PropTypes.node,
    value: PropTypes.any,
    onChange: PropTypes.func,
    visible: PropTypes.bool,
    onToggle: PropTypes.func,
    placeholder: PropTypes.string,
    size: PropTypes.string,
    fullWidth: PropTypes.bool,
};
